import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from '@tanstack/react-router';

import { BrandWordmark } from '@/components/BrandWordmark';
import { company } from '@/content/company';
import { type ThemeChoice, useThemeMode } from '@/context/ThemeModeContext';
import { ROUTES } from '@/shared/routes';
import { PAGE_GUTTER, PAGE_MAX_WIDTH } from '@/styles/layout';

const CHOICES: { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const labelSx = {
  fontSize: 11,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'ig.textMuted',
} as const;

const linkSx = { color: 'ig.textSecondary', fontSize: 14 } as const;

export function SiteFooter() {
  const { choice, setChoice } = useThemeMode();
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{ mt: 8, borderTop: 1, borderColor: 'ig.rule', bgcolor: 'ig.footer' }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: PAGE_MAX_WIDTH,
          py: { xs: 5, md: 8 },
          px: PAGE_GUTTER,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 5,
          fontSize: 14,
          color: 'ig.textMuted',
        }}
      >
        <Stack spacing={1} useFlexGap>
          <BrandWordmark compact sx={{ mb: 1.5 }} />
          <span>{company.name}</span>
          {company.registryCode !== '' && <span>Registry code {company.registryCode}</span>}
          {company.legalAddress !== '' && <span>{company.legalAddress}</span>}
        </Stack>
        <Stack component="nav" aria-label="Company" spacing={1.25} useFlexGap>
          <Typography component="span" sx={labelSx}>
            Company
          </Typography>
          <Link component={RouterLink} to="/marketplace" underline="hover" sx={linkSx}>
            Marketplace
          </Link>
          <Link component={RouterLink} to={ROUTES.about as string} underline="hover" sx={linkSx}>
            About us
          </Link>
          <Link
            component={RouterLink}
            to={ROUTES.howItWorks as string}
            underline="hover"
            sx={linkSx}
          >
            How we work
          </Link>
          <Link href={`mailto:${company.email}`} underline="hover" sx={linkSx}>
            {company.email}
          </Link>
        </Stack>
        <Stack component="nav" aria-label="Legal" spacing={1.25} useFlexGap>
          <Typography component="span" sx={labelSx}>
            Legal
          </Typography>
          <Link component={RouterLink} to={ROUTES.terms as string} underline="hover" sx={linkSx}>
            Terms of Use
          </Link>
          <Link component={RouterLink} to={ROUTES.privacy as string} underline="hover" sx={linkSx}>
            Privacy Policy
          </Link>
          <Link component={RouterLink} to={ROUTES.sla as string} underline="hover" sx={linkSx}>
            Service levels
          </Link>
        </Stack>
      </Container>
      <Box sx={{ borderTop: 1, borderColor: 'ig.rule' }}>
        <Container
          maxWidth={false}
          sx={{
            maxWidth: PAGE_MAX_WIDTH,
            py: 3,
            px: PAGE_GUTTER,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 2,
            fontSize: 12,
            color: 'ig.textMuted',
          }}
        >
          <span>
            © {year} {company.name}
          </span>
          <Box
            role="group"
            aria-label="Appearance"
            sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1.5 }}
          >
            <Typography component="span" sx={labelSx}>
              Appearance
            </Typography>
            <Box sx={{ display: 'flex', border: 1, borderColor: 'ig.rule', borderRadius: '2px' }}>
              {CHOICES.map((c, i) => (
                <Box
                  key={c.value}
                  component="button"
                  type="button"
                  aria-pressed={choice === c.value}
                  onClick={() => setChoice(c.value)}
                  sx={{
                    background: 'none',
                    border: 0,
                    borderLeft: i === 0 ? 0 : 1,
                    borderLeftStyle: 'solid',
                    borderLeftColor: 'ig.rule',
                    px: 1.75,
                    py: 1,
                    font: 'inherit',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: choice === c.value ? 'ig.text' : 'ig.textMuted',
                    bgcolor: choice === c.value ? 'ig.raisedStrong' : 'transparent',
                    '&:focus-visible': {
                      outline: 2,
                      outlineStyle: 'solid',
                      outlineColor: 'ig.accent',
                    },
                  }}
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default SiteFooter;
